import { useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StarRatingInput } from "@/components/StarRating";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewDialog({
  open,
  onOpenChange,
  judgeId,
  judgeName,
  onSuccess,
}) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    overall_rating: 0,
    fairness: 0,
    knowledge: 0,
    communication: 0,
    professionalism: 0,
    comment: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all ratings are provided
    if (
      !formData.overall_rating ||
      !formData.fairness ||
      !formData.knowledge ||
      !formData.communication ||
      !formData.professionalism
    ) {
      toast.error("Please provide all ratings");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/judges/${judgeId}/reviews`, formData);

      toast.success("Review submitted successfully!");
      setFormData({
        overall_rating: 0,
        fairness: 0,
        knowledge: 0,
        communication: 0,
        professionalism: 0,
        comment: "",
      });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  const ratingFields = [
    { key: "overall_rating", label: "Overall Rating" },
    { key: "fairness", label: "Fairness" },
    { key: "knowledge", label: "Knowledge" },
    { key: "communication", label: "Communication" },
    { key: "professionalism", label: "Professionalism" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-navy">
            Review {judgeName}
          </DialogTitle>
        </DialogHeader>

        {!user && (
          <div className="bg-slate-100 p-4 rounded-sm text-sm text-slate-600 mb-4">
            You're submitting anonymously. Sign in to track your reviews.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {ratingFields.map((field) => (
            <div key={field.key}>
              <Label className="text-sm font-medium text-navy mb-2 block">
                {field.label} *
              </Label>
              <StarRatingInput
                value={formData[field.key]}
                onChange={(value) =>
                  setFormData({ ...formData, [field.key]: value })
                }
                size="md"
              />
            </div>
          ))}

          <div>
            <Label htmlFor="comment">Your Review (Optional)</Label>
            <Textarea
              id="comment"
              value={formData.comment}
              onChange={(e) =>
                setFormData({ ...formData, comment: e.target.value })
              }
              placeholder="Share your experience with this judge..."
              className="min-h-[100px] mt-2"
              data-testid="review-comment-input"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gold text-navy hover:bg-gold/90 font-semibold"
              disabled={loading}
              data-testid="submit-review-btn"
            >
              {loading ? "Submitting..." : "Submit Review"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
